import Rest from "Utils/Rest";

import { PubSub, Subscriptions } from "Subscriptions";
import ClientSession from "ClientSession";
import has from 'lodash-es/has';

class Authorization {
    accessType: string;
    authorized: boolean;
}

class FacilitySetting {
    facilitySetting: string;
    authorized: boolean;
    value: string;
}

export default class AccessService {

    static cachedAuthorizations: Authorization[] = [];

    static init = () => {
        PubSub.subscribe(Subscriptions.App.SettingsSubmitted, AccessService.ClearCachedSettings);
    }
    /*
    * @deprecated Use FeatureToggleService.checkFeatureToggle instead for all LaunchDarkley toggles
    * for Users/roles checking - may be replaced with a new method
    */
    static authorize = (accessType: string) => {
        return AccessService.pullAuthorization(accessType);
    }
   /*
   * @deprecated Use FeatureToggleService.checkFeatureToggle instead for all LaunchDarkley toggles
   * for Users/roles checking - may be replaced with a new method
   */
    static authorizeAll = (accessTypes: string[]): Promise<Authorization[]> => {
        return AccessService.pullMultipleAuthorization(accessTypes);
    }
    static checkFacilitySetting = (facilitySetting: string): Promise<boolean> => {
        return AccessService.pullFacilitySetting(facilitySetting, false).then((facSetting) => {
            return facSetting.authorized;
        });
    }

    static checkFacilitySettings = (facilitySettings: string[]): Promise<FacilitySetting[]> => {
        return AccessService.pullMultipleFacilitySettings(facilitySettings);
    }

    static getFacilitySetting = (facilitySetting: string): Promise<string> => {
        return AccessService.pullFacilitySetting(facilitySetting, true).then((facSetting) => {
            return facSetting.value;
        });
    }

    static getAllSettings = () => {
        return Rest.GET("Access/GetAllSettings", {}, {dataType: "json"});
    }

    static allowEmergencyAccess = (): Promise<boolean> => {
        return Rest.GET<boolean>("Access/AllowEmergencyAccess", {});
    }

    static checkUserRole = (userRole: string) => {
        return Rest.GET<boolean>('Access/CheckUserRole', { userRole: userRole });
    }
    /*
     * Helper Methods
     */
    private static pullAuthorization = (accessType: string): Promise<boolean> => {
        let localValue = AccessService.getCachedAuthorization(accessType);

        if (localValue !== null) {
            return Promise.resolve(localValue.authorized);
        }

        return Rest.GET<boolean>("Access/Authorize", { actionString: accessType }).then((authorized) => {
            let newValue = AccessService.cacheAuthorization(accessType, authorized);
            return newValue.authorized;
        });
    }

    private static pullMultipleAuthorization = (accessTypes: string[]): Promise<Authorization[]> => {
        return new Promise<Authorization[]>((resolve, reject) => {
            let allAuths = [];
            let authsToGrab = [];

            for (let accessType of accessTypes) {
                let thisAuth = AccessService.getCachedAuthorization(accessType);

                thisAuth === null ? authsToGrab.push(accessType) : allAuths.push(thisAuth);
            }

            if (authsToGrab.length > 0) {
                Rest.GET<{ accessType: string; authorized: boolean }[]>("Access/AuthorizeAll", { actionStrings: authsToGrab }).then((authorizations) => {
                    for (let auth of authorizations) {
                        let newAuth = AccessService.cacheAuthorization(auth.accessType, auth.authorized);

                        allAuths.push(newAuth);
                    }

                    resolve(allAuths);
                }).catch((err) => {
                    reject(err);
                })
            } else {
                resolve(allAuths);
            }
        });
    }

    private static getCachedAuthorization = (accessType: string): Authorization => {
        let filtered = AccessService.cachedAuthorizations.filter((item) => item.accessType === accessType);

        if (filtered.length > 0) {
            return filtered[0];
        }

        return null;
    }

    private static cacheAuthorization = (accessType: string, authorized: boolean): Authorization => {
        let oldAuth = AccessService.getCachedAuthorization(accessType);

        if (oldAuth !== null) {
            AccessService.cachedAuthorizations.slice(AccessService.cachedAuthorizations.indexOf(oldAuth), 1);
        }

        let newAuth = {
            accessType: accessType,
            authorized: authorized
        };

        AccessService.cachedAuthorizations.push(newAuth);

        return newAuth;
    }

    private static pullFacilitySetting = (facilitySetting: string, getValue: boolean): Promise<FacilitySetting> => {
        return new Promise<FacilitySetting>((resolve, reject) => {
            let localValue = AccessService.getCachedFacilitySetting(facilitySetting);
            resolve(localValue);
        });
    }

    private static pullMultipleFacilitySettings = (facilitySettings: string[]): Promise<FacilitySetting[]> => {
        return new Promise<FacilitySetting[]>((resolve, reject) => {
            let allSettings = [];
            for (let setting of facilitySettings) {
                let thisSetting = AccessService.getCachedFacilitySetting(setting);
                allSettings.push(thisSetting);
            }
            resolve(allSettings);
        });
    }

    private static getCachedFacilitySetting = (facilitySetting: string): FacilitySetting => {
        const settings = ClientSession.rootStore.state['koFacilitySettings'];
        const key = facilitySetting.toLowerCase();
        return has(settings, key)
            ? {facilitySetting: facilitySetting, authorized: true, value: settings[key]}
            : {facilitySetting: facilitySetting, authorized: false, value: null};
    }

    private static ClearCachedSettings() {
        ClientSession.rootStore.dispatch('loadFacilitySettings');
    }
}
